import React, { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import { Box, Card, CardActionArea, CardContent, Tooltip, Typography } from '@mui/material';
import { useTranslation, withTranslation } from 'react-i18next';
import { classNames } from '../../../utils/classNames';
import { pickSoundCardGroupStyles } from './style';
import { getResponsiveSize } from '../../../utils';

export interface pickSoundCardOption {
  Icon: ReactNode;
  label: string;
  cardTooltip?: string;
  tooltipPosition: any;
}

interface PickedCardGroupProps {
  options: pickSoundCardOption[];
  name?: string;
  className?: string;
  title: string;
  setClicks: Dispatch<SetStateAction<boolean>>;
  clicks: boolean;
  plops: boolean;
  setPlops: Dispatch<SetStateAction<boolean>>;
  setBuzzer: Dispatch<SetStateAction<number>>;
  buzzer: number;
  lastBuzzer: number | undefined;
  setSound: Dispatch<SetStateAction<boolean>>;
  sound: boolean;
}

export const PickSoundCardGroup = (props: PickedCardGroupProps) => {
  const {
    className,
    options,
    setSound,
    title,
    setPlops,
    setBuzzer,
    setClicks,
    buzzer,
    clicks,
    plops,
    lastBuzzer,
    sound,
  } = props;
  const { t } = useTranslation();
  const { pickSoundCardGroup } = pickSoundCardGroupStyles;
  const toId = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  const updateSoundState = (newClicks: boolean, newPlops: boolean, newBuzzer: number) => {
    if (!newClicks && !newPlops && newBuzzer === 0) {
      setSound(false);
    } else {
      if (!sound) {
        setSound(true);
      }
    }
  };

  const onChange = (value: string) => {
    let newClicks = clicks;
    let newPlops = plops;
    let newBuzzer = buzzer;

    switch (value) {
      case 'clicks':
        newClicks = !clicks;
        setClicks(newClicks);
        break;
      case 'plops':
        newPlops = !plops;
        setPlops(newPlops);
        break;
      case 'zoomer':
        newBuzzer = buzzer === 0 ? (!!lastBuzzer ? lastBuzzer : 5) : 0;
        setBuzzer(newBuzzer);
        break;
      default:
        console.log('Invalid value');
    }

    updateSoundState(newClicks, newPlops, newBuzzer);
  };

  const isActive = (label: string) => {
    switch (label) {
      case 'clicks':
        return clicks;
      case 'plops':
        return plops;
      case 'zoomer':
        return buzzer !== 0;
      default:
        return false;
    }
  };

  return (
    <Box sx={pickSoundCardGroup} className={classNames(className || '')}>
      <Tooltip
        placement="left-start"
        disableInteractive
        title={
          <Typography
            style={{ fontSize: getResponsiveSize(11) }}
            color="inherit"
            dangerouslySetInnerHTML={{
              __html: `${t('clicks')}: ${t('clicks-tooltip')}<br/>${t('plops')}: ${t('plops-tooltip')}<br/>${t(
                'zoomer'
              )}: ${t('zoomer-tooltip')}`,
            }}
          />
        }
      >
        <Box className="title-container">
          <Typography className="title">{title}</Typography>
        </Box>
      </Tooltip>
      <Box className="items-container items-container-sounds">
        {options?.map((option, index) => (
          <Tooltip
            placement={option.tooltipPosition}
            disableInteractive
            key={`${option?.label}-${title}`}
            title={
              <Typography
                style={{ fontSize: getResponsiveSize(11) }}
                color="inherit"
                dangerouslySetInnerHTML={{ __html: !!option.cardTooltip ? t(`${option.cardTooltip}`) : '' }}
              />
            }
          >
            <Card
              className={classNames(option?.label, { active: isActive(option?.label) }, ['card'])}
              onClick={() => onChange(option?.label)}
              data-testid={`pwid-sound-${toId(option?.label)}`}
            >
              <CardActionArea>
                <Box className="icon"> {option.Icon}</Box>
                <CardContent>
                  <Typography className="desc">{t(`${option?.label}`)}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};
